import React from 'react';
import { useLocation } from 'react-router-dom';
import { useCampus } from './CampusContext';
import { useCampusView } from './useCampusView';
import { buildingForPath, buildingName } from './campusMap';
import type { CampusBuilding } from './campusMap';
import type { CampusPerson } from './campusApi';
import { initialsOf } from './WhoIsHere';
import { desksForBuilding, mayaAllowedFor } from './campusPeople';
import { openBookCall } from './admissions/admissionsCallApi';
import { rungTone } from '../../services/bandLadder';
import { tourFor } from './tourSteps';
import { UpNext, TourOffer } from './home/NextSteps';
import { displayFirstName } from '../../utils/personName';
import { openStaffCard } from './staffCard';
import './campusPageChrome.css';

/**
 * The campus sections at the top of the right rail on a building's page (C11b, the prototype's page rail). C16 (round 3 §2.4):
 * Up next (once; the plan list itself is the side panel's), the tour, "Here now · N" (staff desks and named people, each with 💬
 * to their card and its Message), and the help desk (each desk opens its staff card). The people online list is the contacts rail
 * right under it. Rendered by the cohort contacts rail only
 * while the campus is active; every name and number comes from campus presence, the desks list and the plan.
 *
 * WP-1 (owner rule: always a panel with your next steps and the tours): the rail also carries Up next and the tour card (offer,
 * Resume, taken), the same pieces as the campus home's side panel. It is drawn in the contacts column on wide screens (even with the
 * contacts collapsed), beside the page on the legacy layout, and in the banner's "Next steps" drawer on narrow screens.
 */
export default function CampusRail(): React.ReactElement | null {
  const { active, settings, presence, presenceError, staffDesks, planStops, openPersonCard, openMap, enterThroughDoor, tourProgress, resumeTour, dismissTour } = useCampus();
  const { buildings, audience, accessOf } = useCampusView();
  const { pathname } = useLocation();
  if (!active) return null;
  const here = buildingForPath(pathname);
  const b = here ? buildings.find((x) => x.id === here.id) as CampusBuilding | undefined : undefined;
  const named = (b && presence && !presenceError ? presence[b.id]?.people ?? [] : []).filter((p): p is CampusPerson => 'enrollment_id' in p);
  const deskHere = b ? desksForBuilding(staffDesks, b.id, audience).filter((d) => d.on_duty) : [];
  const allDesks = staffDesks.filter((d) => d.on_duty && (d.id !== 'maya' || mayaAllowedFor(audience)));
  const talk = audience === 'member' || audience === 'explorer'; // C20/C21: "📅 Talk to a person" on the rail's help desk
  const tour = tourFor(audience, buildings, accessOf, settings.workplaceNames);
  return (
    <div className="cm-rail" data-testid="cm-page-rail">
      <UpNext stops={planStops} buildings={buildings} workplaceNames={settings.workplaceNames} hereId={b ? b.id : null}
        onWalk={(id) => openMap(id)} onOpen={(r) => enterThroughDoor(r, null)} />
      <TourOffer audience={audience} stops={tour ? tour.stops : null} buildings={buildings} workplaceNames={settings.workplaceNames}
        running={null} paused={tourProgress.paused} toured={tourProgress.toured} onStart={resumeTour} onDismiss={dismissTour} />
      {b && (
        <section aria-label={`In ${buildingName(b, settings.workplaceNames)}`}>
          <h3 className="cm-rail-h">Here now{presence && !presenceError ? ` · ${(presence[b.id]?.count ?? 0) + deskHere.length}` : ''}</h3>
          <ul className="cm-rail-people">
            {deskHere.map((d) => (
              <li key={d.id}>
                <button type="button" onClick={() => openStaffCard(d.id)} aria-label={`${d.name}, open their staff card`}>
                  <span className="cm-rail-av is-staff" aria-hidden="true">{initialsOf(d.name)}</span>{d.name} <span className="cm-rail-tag">STAFF</span>
                  <span className="cm-rail-msg" aria-hidden="true">💬</span>
                </button>
              </li>
            ))}
            {named.slice(0, 6).map((p) => (
              <li key={p.enrollment_id}>
                <button type="button" onClick={() => openPersonCard({ person: p, buildingId: b.id })} aria-label={`${p.display_name}, open their card`}>
                  <span className="cm-rail-av" aria-hidden="true">{p.avatar_url ? <img src={p.avatar_url} alt="" /> : initialsOf(p.display_name)}</span>
                  {displayFirstName(p.display_name)}
                  {p.rung_name && <span className={`cm-rail-lv rung-${rungTone(p.rung_name)}`}>{p.rung_name}</span>}
                  <span className="cm-rail-msg" aria-hidden="true">💬</span>
                </button>
              </li>
            ))}
            {presenceError ? <li className="cm-rail-quiet" role="status" data-testid="cm-rail-down">Can’t load who’s here right now.</li>
              : !named.length && !deskHere.length && <li className="cm-rail-quiet">Just you in here.</li>}
          </ul>
        </section>
      )}
      {(allDesks.length > 0 || talk) && (
        <section aria-label="Help desk">
          <h3 className="cm-rail-h">Help desk &middot; 24/7</h3>
          <div className="cm-rail-desks">
            {allDesks.map((d) => (
              <button key={d.id} type="button" className="cm-sbtn" onClick={() => openStaffCard(d.id)} aria-label={`${d.name}, ${d.title}, open their staff card`}>
                <span className="cm-sav" aria-hidden="true">{initialsOf(d.name)}</span>{d.name}
              </button>
            ))}
            {talk && (
              <button type="button" className="cm-sbtn" onClick={() => openBookCall()} data-testid="cm-rail-talk" aria-haspopup="dialog">
                <span className="cm-sav" aria-hidden="true">📅</span>Talk to a person
              </button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
